import { IInterval, ISendEmailFeatures } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../../models/AppState';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { Modal } from '../../../../../web/components/Modal';
import { IntervalDropdowns, defaultInterval } from '../../../../components/IntervalDropdowns';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps {
	className?: string;
	isOpen?: boolean;
	isSaving?: boolean;
	onRequestClose?(sendEmailFeatures?: Partial<ISendEmailFeatures>): void;
	sendEmailFeatures?: ISendEmailFeatures;
	styles?: StyleDeclarationValue[];
}

interface IState {
	mondayInterval: IInterval;
	tuesdayInterval: IInterval;
	wednesdayInterval: IInterval;
	thursdayInterval: IInterval;
	fridayInterval: IInterval;
	saturdayInterval: IInterval;
	sundayInterval: IInterval;
	weekdaysInterval: IInterval;
	useDiscreteIntervals: boolean;
}

class _BusinessHours extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = this.getDefaultState();
	}

	public render() {
		const { className, isOpen, isSaving } = this.props;
		const {
			mondayInterval,
			tuesdayInterval,
			wednesdayInterval,
			thursdayInterval,
			fridayInterval,
			saturdayInterval,
			sundayInterval,
			weekdaysInterval,
			useDiscreteIntervals,
		} = this.state;

		return (
			<Modal
				className={`${className || ''}`}
				isOpen={isOpen}
				onRequestClose={this.onRequestClose}
				useDefaultHeader={true}
			>
				<div>Manage Business Hours for this Account</div>
				<div className={css(styleSheet.header)}>
					<Checkbox checked={useDiscreteIntervals} onChange={this.onSelectionChanged}>
						<div>Set individual business hours for each day of the week</div>
					</Checkbox>
				</div>
				<div>
					{useDiscreteIntervals ? (
						<>
							<div className={css(styleSheet.header)}>Monday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={mondayInterval}
								isFriday={false}
								onUpdateInterval={interval => this.onUpdateInterval('mondayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Tuesday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={tuesdayInterval}
								isFriday={false}
								onUpdateInterval={interval => this.onUpdateInterval('tuesdayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Wednesday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={wednesdayInterval}
								isFriday={false}
								onUpdateInterval={interval => this.onUpdateInterval('wednesdayInterval', interval)}
							/>
							<div className={css(styleSheet.header)}>Thursday</div>
							<IntervalDropdowns
								canRemove={true}
								disabled={isSaving}
								interval={thursdayInterval}
								isFriday={false}
								onUpdateInterval={interval => this.onUpdateInterval('thursdayInterval', interval)}
							/>
						</>
					) : (
						<>
							<div className={css(styleSheet.header)}>Weekdays (Monday - Thursday)</div>
							<IntervalDropdowns
								disabled={isSaving}
								interval={weekdaysInterval}
								onUpdateInterval={this.onUpdateWeekdays}
							/>
						</>
					)}
					<div className={css(styleSheet.header)}>Friday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={fridayInterval}
						isFriday={!useDiscreteIntervals}
						onUpdateInterval={interval => this.onUpdateInterval('fridayInterval', interval)}
					/>
					<div className={css(styleSheet.header)}>Saturday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={saturdayInterval}
						onUpdateInterval={interval => this.onUpdateInterval('saturdayInterval', interval)}
					/>
					<div className={css(styleSheet.header)}>Sunday</div>
					<IntervalDropdowns
						canRemove={true}
						disabled={isSaving}
						interval={sundayInterval}
						onUpdateInterval={interval => this.onUpdateInterval('sundayInterval', interval)}
					/>
				</div>
				<button className='cta-button-small' style={{ marginTop: 16 }} onClick={this.onSave}>
					Save
				</button>
			</Modal>
		);
	}

	private getDefaultState = () => {
		const { sendEmailFeatures } = this.props;
		return {
			mondayInterval: sendEmailFeatures?.mondayInterval,
			tuesdayInterval: sendEmailFeatures?.tuesdayInterval,
			wednesdayInterval: sendEmailFeatures?.wednesdayInterval,
			thursdayInterval: sendEmailFeatures?.thursdayInterval,
			fridayInterval: sendEmailFeatures?.fridayInterval,
			saturdayInterval: sendEmailFeatures?.saturdayInterval,
			sundayInterval: sendEmailFeatures?.sundayInterval,
			weekdaysInterval: sendEmailFeatures?.weekdaysInterval ? sendEmailFeatures.weekdaysInterval : defaultInterval,
			useDiscreteIntervals: sendEmailFeatures?.useDiscreteIntervals ?? false,
		};
	};

	private onSelectionChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			this.setState({ useDiscreteIntervals: true });
		} else {
			this.setState({ useDiscreteIntervals: false });
		}
	};

	private onSave = () => {
		const { onRequestClose } = this.props;
		const {
			mondayInterval,
			tuesdayInterval,
			wednesdayInterval,
			thursdayInterval,
			fridayInterval,
			saturdayInterval,
			sundayInterval,
			weekdaysInterval,
			useDiscreteIntervals,
		} = this.state;

		const sendEmailFeatures = {
			mondayInterval,
			tuesdayInterval,
			wednesdayInterval,
			thursdayInterval,
			fridayInterval,
			saturdayInterval,
			sundayInterval,
			weekdaysInterval,
			useDiscreteIntervals,
		};

		onRequestClose(sendEmailFeatures);
	};

	private onRequestClose = () => {
		const { onRequestClose } = this.props;

		this.setState(this.getDefaultState());
		onRequestClose();
	};

	private onUpdateWeekdays = (weekdaysInterval: IInterval) => {
		if (weekdaysInterval.startMinutes < weekdaysInterval.endMinutes) {
			this.setState({ weekdaysInterval });
		} else {
			this.onIntervalError();
		}
	};

	private onUpdateInterval = (key: TypeKeys<IState, IInterval>, interval: IInterval) => {
		if (!interval || interval.startMinutes < interval.endMinutes) {
			this.setState({ [key]: interval } as Pick<IState, TypeKeys<IState, IInterval>>);
		} else {
			this.onIntervalError();
		}
	};

	private onIntervalError = () => {
		const { errorMessages } = this.props;

		errorMessages.push({
			messages: ['End time must be greater than start time.'],
		});
	};
}

const ObservableBusinessHours = observer(_BusinessHours);
export const BusinessHours = inject(ErrorMessagesViewModelKey)(ObservableBusinessHours);
